import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, Textarea, useColorModeValue,
} from "@chakra-ui/react";
import { MdPhotoCamera } from "react-icons/md";
import { updateGroup } from "../groupsApi";

/**
 * Builds the initial form state from the current group.
 * @param {Object} group - Group entity
 * @returns {Object}
 */
function buildInitialForm(group) {
    return {
        name: group?.name || "",
        description: group?.description || "",
        photo: null,
        cover_photo: null,
    };
}

/**
 * Validates the form.
 * @param {Object} form - Form state
 * @returns {string|null} Error message or null
 */
function validateForm(form) {
    if (!form.name?.trim()) return "Le nom du groupe est requis";
    if (form.name.trim().length > 120) return "Le nom du groupe est trop long";
    return null;
}

/**
 * Builds the API payload from the form, omitting unchanged fields.
 * @param {Object} form - Form state
 * @param {Object} group - Original group
 * @returns {Object}
 */
function buildPayload(form, group) {
    const payload = {};
    if (form.name?.trim() !== group?.name) payload.name = form.name.trim();
    if (form.description !== (group?.description || "")) payload.description = form.description || "";
    if (form.photo instanceof File) payload.photo = form.photo;
    if (form.cover_photo instanceof File) payload.cover_photo = form.cover_photo;
    return payload;
}

/**
 * Submits the payload and surfaces toasts.
 * @param {Object} args - Submit args
 */
async function submitUpdate({ groupId, form, group, setLoading, onClose, onUpdated }) {
    const err = validateForm(form);
    if (err) { toast.error(err); return; }
    const payload = buildPayload(form, group);
    if (Object.keys(payload).length === 0) { toast("Aucune modification"); return; }
    setLoading(true);
    try {
        const updated = await updateGroup(groupId, payload);
        toast.success("Groupe mis à jour");
        onUpdated?.(updated);
        onClose();
    } catch (error) {
        const msg = error?.response?.data;
        toast.error(typeof msg === "string" ? msg : "Échec de la mise à jour");
    } finally {
        setLoading(false);
    }
}

/**
 * Generic file picker row with a label-wrapped hidden file input.
 * @param {Object} props - { label, file, onPick }
 */
function FilePickerRow({ label, file, onPick }) {
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex align="center" gap="12px" flexWrap="wrap">
            <Button as="label" size="sm" variant="outline" colorScheme="brand" leftIcon={<MdPhotoCamera />} cursor="pointer">
                {label}
                <Input type="file" accept="image/*" display="none" onChange={(e) => onPick(e.target.files?.[0] || null)} />
            </Button>
            <Text color={subColor} fontSize="0.78rem">{file ? file.name : "Aucun fichier"}</Text>
        </Flex>
    );
}

/**
 * Body of the edit form.
 * @param {Object} props - { form, onChange }
 */
function DialogBody({ form, onChange }) {
    return (
        <Stack spacing="16px">
            <FormControl isRequired>
                <FormLabel fontSize="0.8rem">Nom</FormLabel>
                <Input size="sm" borderRadius="8px" value={form.name} onChange={(e) => onChange("name", e.target.value)} />
            </FormControl>
            <FormControl>
                <FormLabel fontSize="0.8rem">Description</FormLabel>
                <Textarea size="sm" borderRadius="8px" rows={3} value={form.description} onChange={(e) => onChange("description", e.target.value)} />
            </FormControl>
            <FilePickerRow label="Photo" file={form.photo} onPick={(f) => onChange("photo", f)} />
            <FilePickerRow label="Cover" file={form.cover_photo} onPick={(f) => onChange("cover_photo", f)} />
        </Stack>
    );
}

/**
 * Dialog allowing admins to edit a group's profile.
 * @param {Object} props - { open, group, onClose, onUpdated }
 */
function EditGroupDialog({ open, group, onClose, onUpdated }) {
    const [form, setForm] = useState(buildInitialForm(group));
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setForm(buildInitialForm(group)); }, [open, group]);

    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submitUpdate({ groupId: group?.id, form, group, setLoading, onClose, onUpdated });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Modifier le groupe</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody form={form} onChange={onChange} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Enregistrement...">Enregistrer</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default EditGroupDialog;
